import React from 'react';
import classNames from 'classnames';

// Section components
import SectionArticle from './SectionArticle';
import SectionCarousel from './SectionCarousel';
import SectionColumns from './SectionColumns';
import SectionFeatures from './SectionFeatures';
import SectionHero from './SectionHero';
import SectionListings from './SectionListings';

// Styles
// Note: these contain
// - shared classes that are passed as defaultClasses
// - dark theme overrides
// TODO: alternatively, we could consider more in-place way of theming components
import css from './SectionBuilder.module.css';
import SectionFooter from './SectionFooter';

// These are shared classes.
// Use these to have consistent styles between different section components
// E.g. share the same title styles
const DEFAULT_CLASSES = {
  sectionDetails: css.sectionDetails,
  title: css.title,
  description: css.description,
  ctaButton: css.ctaButton,
  blockContainer: css.blockContainer,
  defaultLink: css.defaultLink,
};

/////////////////////////////////////////////
// Mapping of section types and components //
/////////////////////////////////////////////

const defaultSectionComponents = {
  article: { component: SectionArticle },
  carousel: { component: SectionCarousel },
  columns: { component: SectionColumns },
  features: { component: SectionFeatures },
  footer: { component: SectionFooter },
  hero: { component: SectionHero },
  listings: { component: SectionListings },
};

//////////////////////
// Section builder //
//////////////////////

/**
 * @typedef {Object} FieldOption
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * @typedef {Object} BlockOption
 * @property {ReactNode} component
 */

/**
 * @typedef {Object} SectionOption
 * @property {ReactNode} component
 */

/**
 * @typedef {Object} SectionConfig
 * @property {string} sectionId
 * @property {string} sectionName
 * @property {('article' | 'carousel' | 'columns' | 'features' | 'hero' | 'listings')} sectionType
 */

/**
 * Build section elements from given section config array.
 *
 * @component
 * @param {Object} props
 * @param {Array<SectionConfig>} props.sections
 * @param {Object} props.options
 * @param {Object<string,FieldOption>} props.options.fieldComponents
 * @param {Object<string,BlockOption>} props.options.blockComponents
 * @param {Object<string,SectionOption>} props.options.sectionComponents
 * @param {boolean} props.options.isInsideContainer
 * @param {ReactNode?} props.afterFirstSectionHeader content placed after the first page introduction
 * @param {string?} props.afterSectionId section after which custom content is placed
 * @param {ReactNode?} props.afterSectionContent custom content placed after the selected section
 * @returns {JSX.Element} element containing array of sections according from given config array.
 */
const SectionBuilder = props => {
  const {
    sections = [],
    options,
    afterFirstSectionHeader,
    afterSectionId,
    afterSectionContent,
  } = props;
  const { sectionComponents = {}, isInsideContainer, ...otherOption } = options || {};

  // If there's no sections, we can't render the correct section component
  if (!sections || sections.length === 0) {
    return null;
  }

  // Selection of Section components
  const components = { ...defaultSectionComponents, ...sectionComponents };
  const getComponent = sectionType => {
    const config = components[sectionType];
    return config?.component;
  };

  // Generate unique ids for sections if operator has managed to create duplicates
  // E.g. "foobar", "foobar1", and "foobar2"
  const sectionIds = [];
  const getUniqueSectionId = (sectionId, index) => {
    const candidate = sectionId || `section-${index + 1}`;
    if (sectionIds.includes(candidate)) {
      let sequentialCandidate = `${candidate}1`;
      for (let i = 2; sectionIds.includes(sequentialCandidate); i++) {
        sequentialCandidate = `${candidate}${i}`;
      }
      return getUniqueSectionId(sequentialCandidate, index);
    } else {
      sectionIds.push(candidate);
      return candidate;
    }
  };

  // Resolve all section ids
  const sectionsWithResolvedIds = sections.map((section, index) => ({
    ...section,
    sectionId: getUniqueSectionId(section.sectionId, index),
  }));

  return (
    <>
      {sectionsWithResolvedIds.map((section, index) => {
        const Section = getComponent(section.sectionType);
        // If the default "dark" theme should be applied (when text color is white).
        // By default, this information is stored to customAppearance field
        const isDarkTheme =
          section?.appearance?.fieldType === 'customAppearance' &&
          section?.appearance?.textColor === 'white';
        const classes = classNames(section.className, { [css.darkTheme]: isDarkTheme });
        const sectionId = section.sectionId;
        const isFirstArticle = index === 0 && section.sectionType === 'article';
        const contentAfterSection = index === 0 && !isFirstArticle ? afterFirstSectionHeader : null;
        const selectedSectionContent = sectionId === afterSectionId ? afterSectionContent : null;
        const sectionDefaultClasses = {
          ...DEFAULT_CLASSES,
          sectionDetails: classNames(
            DEFAULT_CLASSES.sectionDetails,
            section.sectionDetailsClassName
          ),
          title: classNames(DEFAULT_CLASSES.title, section.titleClassName),
          description: classNames(DEFAULT_CLASSES.description, section.descriptionClassName),
          blockContainer: classNames(
            DEFAULT_CLASSES.blockContainer,
            section.blockContainerClassName
          ),
          block: section.blockClassName,
          blockText: section.blockTextClassName,
          blockTitle: section.blockTitleClassName,
          blockContent: section.blockContentClassName,
        };

        if (Section) {
          return (
            <React.Fragment key={`${sectionId}_i${index}`}>
              <Section
                defaultClasses={sectionDefaultClasses}
                isInsideContainer={isInsideContainer}
                options={{ ...otherOption, defaultClasses: sectionDefaultClasses }}
                {...section}
                className={classes}
                sectionId={sectionId}
                allSections={sectionsWithResolvedIds}
                afterHeader={isFirstArticle ? afterFirstSectionHeader : null}
              />
              {contentAfterSection}
              {selectedSectionContent}
            </React.Fragment>
          );
        } else {
          // If the section type is unknown, the app can't know what to render
          console.warn(
            `Unknown section type (${section.sectionType}) detected using sectionName (${section.sectionName}).`
          );
          return null;
        }
      })}
    </>
  );
};

export default SectionBuilder;
